/**
 * A camada de luz (techspec-dia-e-noite). Por quadro, só quando está escuro:
 *  1. um canvas do tamanho da tela é preenchido com a cor do ambiente;
 *  2. cada fonte de luz visível soma a textura dela (a do `light.mul` da UO, tingida), em modo aditivo;
 *  3. o canvas multiplica o mundo.
 * De dia (nível 0) não faz nada e não custa nada.
 */
import type { LuzNoMapa, MapaCarregado } from './mapa';
import { wx, wy } from './desenho';
import { blocosVisiveis, Z_PX } from './blocos';
import { luzesDaMobilia } from './casas';

type Ctx2D = CanvasRenderingContext2D;

interface AtlasLuz {
  imagem: HTMLImageElement;
  rects: Map<number, readonly [number, number, number, number]>;
}

let atlas: AtlasLuz | null = null;
let carregando = false;

function carregarAtlas(): void {
  if (atlas || carregando) return;
  carregando = true;
  void (async () => {
    try {
      const r = await fetch('/luz/luzes.json');
      if (!r.ok) return;
      const raw = await r.json() as Record<string, unknown>;
      const rects = new Map<number, readonly [number, number, number, number]>();
      for (const [k, v] of Object.entries(raw)) {
        const id = Number(k);
        if (Number.isInteger(id) && Array.isArray(v) && v.length === 4 && v.every(n => Number.isFinite(n)))
          rects.set(id, [v[0], v[1], v[2], v[3]] as const);
      }
      const imagem = new Image();
      imagem.src = '/luz/luzes.png';
      await imagem.decode();
      atlas = { imagem, rects };
    } catch (err) {
      console.warn('[luz] texturas de luz não carregaram; a noite fica sem luzes', err);
    }
  })();
}

/** Textura já tingida, por (id da luz, cor). */
const tingidas = new Map<string, HTMLCanvasElement>();
function tingida(id: number, cor: string): HTMLCanvasElement | null {
  if (!atlas) return null;
  const chave = `${id}|${cor}`;
  const hit = tingidas.get(chave);
  if (hit) return hit;
  const r = atlas.rects.get(id);
  if (!r) return null;
  const c = document.createElement('canvas');
  c.width = r[2];
  c.height = r[3];
  const g = c.getContext('2d');
  if (!g) return null;
  g.drawImage(atlas.imagem, r[0], r[1], r[2], r[3], 0, 0, r[2], r[3]);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = cor;
  g.fillRect(0, 0, c.width, c.height);
  tingidas.set(chave, c);
  return c;
}

let camada: HTMLCanvasElement | null = null;

/**
 * As luzes de janela do `light.mul` (26, 27, 40, 42): texturas direcionais, feitas para sair do chão da casa, como o
 * cliente da UO desenha (centro do tile). Erguidas até a altura do item, o brilho caía em cima da própria janela.
 */
const LUZ_DE_JANELA: ReadonlySet<number> = new Set([26, 27, 40, 42]);
/** Tocha, arandela, braseiro, vela (as que tremulam): o círculo da UO é curto para o mapa visto de cima. */
const ESCALA_FOGO = 1.6;

/**
 * Escurece o mundo já desenhado em `ctx` e acende as luzes. `nivel` é o da UO (0 claro .. 30 escuro), `cor` o ambiente
 * já calculado. O `ctx` está na escala CSS (o `setTransform(dpr)` do motor).
 */
export function aplicarLuz(
  ctx: Ctx2D, mapa: MapaCarregado, cam: { x: number; y: number }, vw: number, vh: number, zoom: number,
  nivel: number, cor: readonly [number, number, number], agora: number
): void {
  if (nivel <= 0.2) return;
  carregarAtlas();
  if (!camada) camada = document.createElement('canvas');
  const w = Math.max(1, Math.ceil(vw)), h = Math.max(1, Math.ceil(vh));
  if (camada.width !== w || camada.height !== h) { camada.width = w; camada.height = h; }
  const g = camada.getContext('2d');
  if (!g) return;
  g.globalCompositeOperation = 'source-over';
  g.globalAlpha = 1;
  g.fillStyle = `rgb(${Math.round(cor[0] * 255)},${Math.round(cor[1] * 255)},${Math.round(cor[2] * 255)})`;
  g.fillRect(0, 0, w, h);

  if (atlas) {
    const forca = Math.min(1, (nivel / 26) * 1.15);
    g.globalCompositeOperation = 'lighter';
    const TW = mapa.tileW, TH = mapa.tileH;
    const vistas: LuzNoMapa[] = [];
    // a luz vai além do item: os blocos em volta da tela também acendem o que está nela
    for (const { bx, by } of blocosVisiveis(mapa, cam, vw * 2, vh * 2, zoom))
      for (const l of mapa.luzesNoBloco(bx, by)) vistas.push(l);
    // A mobília acesa das casas (vela, braseiro…), que não é estático do mapa.
    for (const l of luzesDaMobilia(mapa)) vistas.push(l);
    for (const l of vistas) {
      const tex = tingida(l.luz, l.cor);
      if (!tex) continue;
      const pisca = l.tremula ? 0.88 + 0.08 * Math.sin(agora / 90 + l.x * 7 + l.y * 3) + 0.06 * Math.sin(agora / 37 + l.x) : 1;
      const base = wy(l.x, l.y, TH) + TH - l.z * Z_PX;
      const janela = LUZ_DE_JANELA.has(l.luz);
      const cx = (wx(l.x, l.y, TW) - cam.x) * zoom + vw / 2;
      // a janela acende a partir do chão (o centro do tile); o resto, da altura da chama
      const cy = (base - (janela ? TH / 2 : l.h * 0.62) - cam.y) * zoom + vh / 2;
      const escala = l.tremula ? ESCALA_FOGO : 1;
      const lw = tex.width * zoom * escala, lh = tex.height * zoom * escala;
      if (cx + lw / 2 < 0 || cx - lw / 2 > vw || cy + lh / 2 < 0 || cy - lh / 2 > vh) continue;
      g.globalAlpha = forca * pisca;
      g.drawImage(tex, cx - lw / 2, cy - lh / 2, lw, lh);
      // segunda passada: a textura da UO é suave demais sobre o céu escuro
      g.globalAlpha = forca * pisca * 0.6;
      g.drawImage(tex, cx - lw / 2, cy - lh / 2, lw, lh);
    }
    g.globalAlpha = 1;
  }

  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(camada, 0, 0, vw, vh);
  ctx.restore();
}
